<template>
	<Application :name="$t('text.completeRegistration')" data-cy="invitation-registration-view" :loading="loading" :errorDetail="errorDetail" v-model:errorTitle="errorTitle" :successDetail="successDetail" v-model:successTitle="successTitle" class="main">
		
		<!-- we need to put this empty template otherwise the navbar is empty/transparent. -->
		<template #navbar>

		</template>

		<!-- Registration Form -->
		<v-row justify="center" v-if="invitationData">
			<v-card id="card" class="page elevation-4">
				<v-card-text style="margin:0px;padding:0px">
					<h1>{{ $t('text.completeRegistration') }}</h1>
					<p class="subTitle" v-html="$t('text.youHaveBeenInvited')" style="margin-left:0px"></p>
					
					<!-- Invitation Details -->
					<div class="invitation-info">
						<div>
							<v-label>{{ $t('text.email') }}</v-label>
							<div data-cy="invitation-email" class="info-value">{{ invitationData.email }}</div>
						</div>
						<div>
							<v-label>{{ $t('text.role') }}</v-label>
							<div data-cy="invitation-role" class="info-value">{{ invitationData.role }}</div>
						</div>
					</div>

					<v-form @submit.prevent="handleRegistration">
						<div class="field left-border">
							<v-label>{{ $t('text.password') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
							<v-text-field
								v-model="formData.password"
								type="password"
								variant="outlined"
								density="compact"
								required
								autocomplete="new-password"
								:error-messages="passwordErrors"
								:hint="passwordPolicyHint()"
								persistent-hint
								hide-details="auto"
								@update:modelValue="onPasswordInput"
								@blur="v$.formData.password.$touch()"
								data-cy="password"
								id="invitePassword"
							/>
						</div>
						
						<div class="field left-border">
							<v-label>{{ $t('text.repeatPassword') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
							<v-text-field
								v-model="formData.confirmPassword"
								type="password"
								variant="outlined"
								density="compact"
								required
								autocomplete="new-password"
								:error-messages="confirmPasswordErrors"
								:hide-details="confirmPasswordErrors.length === 0"
								@update:modelValue="v$.formData.confirmPassword.$touch()"
								@blur="v$.formData.confirmPassword.$touch()"
								data-cy="confirmPassword"
								id="inviteConfirmPassword"
							/>
						</div>

						<div class="field left-border">
							<v-label>{{ $t('text.preferredLanguage') }} <span class="error-text">({{ $t('text.required') }})</span></v-label>
							<v-select
								v-model="formData.preferredLocale"
								:items="localeOptions"
								item-title="label"
								item-value="value"
								variant="outlined"
								density="compact"
								autocomplete="off"
								:placeholder="`${$t('text.choose')}...`"
								:error-messages="preferredLanguageErrors"
								:hide-details="preferredLanguageErrors.length === 0"
								@update:modelValue="v$.formData.preferredLocale.$touch()"
								@blur="v$.formData.preferredLocale.$touch()"
								data-cy="preferredLanguage"
								id="invitePreferredLanguage"
							>
								<template v-slot:item="{ props, item }">
									<v-list-item v-bind="props" class="preferred-language-list-item">
										<div style="display: flex; align-items: center;">
											<LanguageFlag v-model="item.value" />
											<span style="margin-left: 10px;">{{ item.title }}</span>
										</div>
									</v-list-item>
								</template>
								<template v-slot:selection="{ item }">
									<div style="display: flex; align-items: center;">
										<LanguageFlag v-model="item.value" />
										<span style="margin-left: 10px;">{{ item.title }}</span>
									</div>
								</template>
							</v-select>
							<p class="helpText" v-html="$t('text.preferredLanguageHelp')"/>
						</div>

						<v-row align="center" justify="center" style="margin-top:20px">
							<v-btn
								type="submit"
								variant="flat"
								theme="dark"
								class="activeButton"
								:loading="submitting"
								:disabled="!isFormValid"
								data-cy="complete-registration"
								id="btnCompleteRegistration"
							>
								{{ $t('text.completeRegistration') }}
							</v-btn>
						</v-row>
					</v-form>
				</v-card-text>
			</v-card>
		</v-row>
	</Application>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, sameAs } from '@vuelidate/validators'
import InvitationExecutive from '../../../../api2/src/modules/trade-hub/InvitationExecutive'
import LanguageFlag from '@/components/common/LanguageFlag.vue'
import Application from '../applications/Application.vue'
import Authorization from '@/mixins/Authorization.vue'
import Common from '@/mixins/Common.vue'
import PasswordPolicy from '@/mixins/PasswordPolicy.vue'
import OIDC from '../main/OIDC.ts'

export default {
	name: 'InvitationRegistration',
	components: { LanguageFlag, Application },
	mixins: [ Common, Authorization, PasswordPolicy ],
	validations() {
		return {
			formData: {
				password: { required, passwordPolicy: this.passwordPolicyRule(this.invitationData?.email) },
				confirmPassword: { required, sameAsPassword: sameAs(this.formData.password) },
				preferredLocale: { required }
			}
		}
	},
	setup() {
		return {
			v$: useVuelidate()
		}
	},
	data() {
		return {
			loading: true,
			errorTitle: '',
			errorDetail: '',
			successTitle: '',
			successDetail: '',
			submitting: false,
			invitationData: null,
			passwordApiErrors: [],
			oidc: null,
			formData: {
				password: '',
				confirmPassword: '',
				preferredLocale: 'en'
			},
			localeOptions: [
				{ label: 'English', value: 'en' },
				{ label: 'Deutsch', value: 'de' },
				{ label: 'Français', value: 'fr' },
				{ label: 'Italiano', value: 'it' },
				{ label: 'Nederlands', value: 'nl' }
			]
		}
	},
	watch: {
		'formData.preferredLocale'(newLocale) {
			this.setLocaleFromInvitation()
		}
	},
	computed: {
		token() {
			return this.$route.query.token
		},
		passwordErrors() {
			const errors = []
			const { $dirty, required } = this.v$.formData.password

			if (!$dirty) return this.passwordApiErrors

			if (required.$invalid) errors.push(this.$t('text.passwordRequired'))
			errors.push(...this.passwordPolicyMessages(this.formData.password, this.invitationData?.email), ...this.passwordApiErrors)

			return errors
		},
		confirmPasswordErrors() {
			const errors = []
			const { $dirty, sameAsPassword } = this.v$.formData.confirmPassword

			if (!$dirty) return errors

			if (sameAsPassword.$invalid) errors.push(this.$t('text.repeatPasswordSame'))

			return errors
		},
		preferredLanguageErrors() {
			const errors = []
			const { $dirty, required } = this.v$.formData.preferredLocale

			if (!$dirty) return errors

			if (required.$invalid) errors.push(this.$t('text.preferredLanguageRequired'))

			return errors
		},
		isFormValid() {
			return this.formData.password && 
				   this.formData.confirmPassword && 
				   this.formData.password === this.formData.confirmPassword &&
				   !this.v$.formData.password.$invalid &&
				   this.formData.preferredLocale
		}
	},
	async mounted() {
		this.oidc = await OIDC.init(this.$route.query)
		
		this.setLocaleFromInvitation()
		
		if (!this.token) {
			this.errorTitle = this.$t('text.invalidInvitation')
			this.errorDetail = this.$t('text.noTokenProvided')
			return
		}
		await this.validateToken()
	},
	methods: {
		async validateToken() {
			this.loading = true
			try {
				const executive = new InvitationExecutive(this)
				this.invitationData = await executive.resolveInvitationToken(this.token)
				
				// Set default locale from invitation (watcher will update i18n locale)
				this.formData.preferredLocale = this.invitationData.preferredLocale || 'de'
			} catch (error) {
				console.error('Token validation failed:', error)
				this.handleTokenError(error)
			} finally {
				this.loading = false
			}
		},
		handleTokenError(error) {
			const errorMessage = error.message || error
			if (errorMessage.includes('invalid-or-used-token')) {
				this.errorTitle = this.$t('text.invitationNotValid')
				this.errorDetail = this.$t('text.invitationAlreadyUsedOrCancelled')
			} else if (errorMessage.includes('token-expired')) {
				this.errorTitle = this.$t('text.invitationExpired')
				this.errorDetail = this.$t('text.invitationExpiredMessage')
			} else {
				this.errorTitle = this.$t('text.invalidInvitation')
				this.errorDetail = this.$t('text.somethingWentWrong')
			}
			setTimeout(() => {
				this.$root.app.showLogin()
			}, 5000)
		},
		async handleRegistration() {
			if (!this.isFormValid) return

			// Touch all form fields to show validation errors
			this.v$.$touch()
			
			if (this.passwordErrors.length !== 0 || 
				this.confirmPasswordErrors.length !== 0 || 
				this.preferredLanguageErrors.length !== 0) {
				return
			}

			this.submitting = true
			try {
				const executive = new InvitationExecutive(this)
				const result = await executive.acceptInvitation({
					token: this.token,
					password: this.formData.password,
					preferredLocale: this.formData.preferredLocale
				})

				// Set success message
				this.successTitle = this.$t('text.ACCOUNT_CREATED')
				this.successDetail = this.$t('text.registrationSuccess')

				// Auto-login the user (similar to main registration flow)
				const loginOk = await this.login(this.invitationData.email, this.formData.password)
				
				if (this.oidc) {
					await OIDC.complete(this.oidc, this.$store.state.loggedInUser.kc_token)
					return
				}
				
				if (loginOk) {
					// Redirect to dashboard like main registration
					this.$emit('show-dashboard')
				} else {
					// Fallback to manual login redirect if auto-login fails
					window.location.href = process.env.VUE_APP_LOGIN_URL || 'https://myservices.alturos.com'
				}

			} catch (error) {
				console.error('Registration failed:', error)
				// MYS-7479: e.g. a common password, which only Keycloak can reject; the invitation stays open for a retry
				this.passwordApiErrors = this.passwordPolicyApiMessages(error)
				this.errorTitle = this.$t('text.registrationFailed')
				this.errorDetail = this.passwordApiErrors.join(' ') || error.message || this.$t('text.somethingWentWrong')
			} finally {
				this.submitting = false
			}
		},
		onPasswordInput() {
			this.passwordApiErrors = []
			this.v$.formData.password.$touch()
		},
		setLocaleFromInvitation() {
			this.$root.$i18n.locale = this.formData.preferredLocale
			this.$vuetify.locale.current = this.formData.preferredLocale
			this.$store.commit('setSelectedLocale', this.formData.preferredLocale)
		}
	}
}
</script>

<style scoped>
.page { 
	width: 100%; 
	max-width: 600px; 
}

.invitation-info {
	background: #f5f5f5;
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 24px;
	display: flex;
	flex-direction: column;
	gap: 8px;
	border: 2px dashed #d0d0d0;
}

.info-value {
	background: #fafafa;
	padding: 12px;
	border-radius: 4px;
	border: 1px solid #d0d0d0;
	margin-top: 8px;
	font-weight: 500;
	color: #888;
	cursor: not-allowed;
	user-select: none;
	opacity: 0.85;
	background-image: repeating-linear-gradient(
		45deg,
		transparent,
		transparent 10px,
		rgba(200, 200, 200, 0.05) 10px,
		rgba(200, 200, 200, 0.05) 20px
	);
}

.activeButton {
	height: 48px !important;
	background: #62c522 !important;
	border: thin #58b01e solid !important;
	display: flex !important;
	justify-content: space-around !important;
	align-items: center !important;
	border-radius: 5px !important;
	font-family: 'Inter', sans-serif;
	font-size: 12pt !important;
	font-weight: normal !important;
	letter-spacing: 0 !important;
	text-transform: none !important;
}

.error {
	color: red;
}

h1 {
	margin-bottom: 16px;
}

.subTitle {
	color: #666;
	margin-bottom: 24px;
	line-height: 1.5;
}

.field {
	margin-bottom: 20px;
}

.error-text {
	color: red;
	font-size: 0.875em;
}

.helpText {
	margin-top: 8px;
	color: #666;
	font-size: 0.875em;
	line-height: 1.4;
}

@media (max-width: 600px) {
	.page {
		margin: 0;
	}
}
</style>

<style lang="scss">
.v-list:has(.preferred-language-list-item) {
	padding: 0px;
	.preferred-language-list-item {
		padding-inline: 12px;
		min-height: 60px;
		padding-top: 7px;
		padding-bottom: 7px;
		border-bottom: 1px solid #e5e5e5;

		&:hover {
			cursor: pointer !important;
		}
		.v-list-item__overlay,
		.v-list-item-title,
		.v-list-item__spacer {
			display: none;
		}
		.v-list-item-action {
			margin-inline-end: 0px;
		}
		&.v-list-item--active {
			color: inherit !important;
		}
	}
}
</style>
